<template>
    <div>
        <!--Input-->
        <div class="postcode">
            <div class="postcode--input">
                <!-- Postcode input field -->
                <div class="input--no-margin" :class="postcodeInputClass">
                    <label
                        class="input__label"
                        v-if="showLabel"
                        :class="{ 'required-input': mandatory }"
                        :for="`${_uid}-postcode`"
                        v-html="$t('POSTCODE_FORM_POSTCODE')"/>
                    <input
                        :id="`${_uid}-postcode`"
                        autocomplete="shipping postal-code"
                        type="text"
                        class="input__field"
                        v-model.lazy="postcode"
                        v-on:keyup="$emit('clear-address', true)"
                        v-on:keyup.enter.prevent="findAvailableAddresses()">
                </div>
            </div>
                <!-- Postcode button -->
                <button
                    type="button"
                    class="btn btn--green postcode--button"
                    @click="findAvailableAddresses()"
                    v-html="$t('POSTCODE_FORM_FIND_ADDRESS')"/>
        </div>

        <!--Select & error-->
        <div>
            <div class="w-full input input--invalid">
                <!-- Validation -->
                <p class="input__message"
                   v-if="postcodeEmpty"
                   v-html="$t('POSTCODE_FORM_PLEASE_PUT_POSTCODE')"/>
                <p class="input__message"
                   v-if="errorHandler.has('delivery_address_postcode')"
                   v-html="$t(errorHandler.get('delivery_address_postcode'))"/>
                <p class="input__message"
                   v-if="!isPostcodeValid || noAddresses"
                   v-html="$t('POSTCODE_FORM_AFRAID_THAT_IS_NOT')"/>
            </div>

            <div v-if="addresses" class="w-full input" :class="{noPadding}">
                <Dropdown
                    v-model="selectedAddress"
                    :inputStyle="'border-color: grey;'"
                    :track-by="'summaryline'"
                    :label="'summaryline'"
                    :addressDropdown="true"
                    :placeholder="$t('POSTCODE_FORM_PLEASE_SELECT_ADDRESS')"
                    :items="addresses" />
            </div>
        </div>
    </div>
</template>

<script>
import { Dropdown } from '@freddiesflowers/frontend-component-library';
import { formatPostcode, isPostcodeFormatValid } from '@/helpers/PostcodeHelpers';
import PostcodeLookupService from '@/services/logistics/PostcodeLookupService';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';

export default {
    name: 'PostcodeLookupField',
    data: () => {
        return {
            postcode: null,
            addresses: null,
            selectedAddress: null,
            noAddresses: false,
        };
    },
    props: {
        value: {
            required: true,
        },
        countryIso: {
            required: false,
            default: 'GBR',
        },
        mandatory: {
            default: false,
            type: Boolean,
        },
        showLabel: {
            default: true,
            type: Boolean,
        },
        noPadding: {
            default: false,
            type: Boolean,
        },
        errorHandler: {
            required: false,
            default: () => new ErrorResponseHandler(),
            validator: function(value) {
                return value instanceof ErrorResponseHandler;
            },
        },
    },
    computed: {
        isPostcodeValid() {
            return this.postcode === null || isPostcodeFormatValid(this.postcode);
        },
        isValid() {
            return this.isPostcodeValid && this.noAddresses === false;
        },
        postcodeInputClass() {
            let inputClass = this.noPadding ? 'noPadding' : '';
            if (this.postcode === null) {
                return `${inputClass} input`;
            }

            return this.isPostcodeValid && this.noAddresses === false && this.postcodeEmpty === false ? `input input--success ${inputClass}` : `input input--invalid ${inputClass}`;
        },
        userHasFilled() {
            return this.postcode !== null;
        },
        postcodeEmpty() {
            return this.userHasFilled && (this.postcode || '').length < 1;
        },
    },
    methods: {
        findAvailableAddresses() {
            this.addresses = null;
            this.noAddresses = false;

            if (this.userHasFilled && this.isPostcodeValid) {
                PostcodeLookupService.findAddressesForPostcode(this.postcode)
                    .then(results => {
                        if (results.data.length > 0) {
                            this.addresses = results.data;
                        } else {
                            this.noAddresses = true;
                        }
                    })
                    .catch(error => console.error(error));
            }
        },

        formatPostcode() {
            if (isPostcodeFormatValid(this.postcode)) {
                this.postcode = formatPostcode(this.postcode);
            }
        },
    },
    watch: {
        postcode(value) {
            this.$emit('input', value);
            this.formatPostcode();
            this.noAddresses = false;
        },
        value: {
            immediate: true,
            handler(value) {
                this.postcode = value;
            },
        },
        selectedAddress(address) {
            this.$emit('address-selected', address);
        },
    },
    components: {
        Dropdown,
    },
};
</script>

<style lang="scss" scoped>
.postcode {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    &--button {
        margin-left: 10px;
        min-width: 150px;
        @media (min-width: 768px) {
            min-width: 200px;
        }
    }
    &--input {
        width: 100%;
    }
}
.noPadding {
    padding: 0;
}
</style>
